import React from "react";
import { displayInvoiceNumber } from "../../utils/invoiceNumber";

/**
 * Asked when a client picked on a NEW invoice already has a draft.
 *
 * Firm feedback, 2026-10-30: "If a draft is started, and another one is
 * started, there should be a warning that 'a draft for this company has
 * already been started' -- 'do you want to create another draft' or 'do you
 * want to edit the existing draft'." Two drafts for one client is how the
 * same work gets billed twice.
 *
 * Styled as PageBreakChoice, the other question this page asks.
 */

const BUTTON = {
  padding: "9px 16px",
  borderRadius: 6,
  fontSize: 13,
  cursor: "pointer",
  fontWeight: 600,
};

const money = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n)
    ? `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "—";
};

const describe = (d) =>
  [displayInvoiceNumber(d.number), d.date, money(d.total)].filter(Boolean).join(" · ");

export default function ExistingDraftChoice({ clientName, drafts, onEdit, onCreateAnother }) {
  if (!drafts || !drafts.length) return null;
  const one = drafts.length === 1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="A draft has already been started"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(17,24,39,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: 10,
          padding: 24,
          maxWidth: 460,
          width: "100%",
          boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
        }}
      >
        <h3 style={{ margin: "0 0 8px", fontSize: 17 }}>
          A draft for {clientName || "this client"} has already been started.
        </h3>
        <p style={{ margin: "0 0 14px", color: "#4b5563", fontSize: 14, lineHeight: 1.5 }}>
          {one
            ? "Do you want to edit the existing draft, or create another one?"
            : `There are ${drafts.length} drafts. Pick one to edit, or create another.`}
        </p>

        {one ? (
          <p style={{ margin: "0 0 18px", fontSize: 13, color: "#374151" }}>
            {describe(drafts[0])}
          </p>
        ) : (
          <ul style={{ listStyle: "none", margin: "0 0 18px", padding: 0, maxHeight: 240, overflowY: "auto" }}>
            {drafts.map((d) => (
              <li key={d.id} style={{ marginBottom: 6 }}>
                <button
                  type="button"
                  onClick={() => onEdit(d)}
                  style={{
                    ...BUTTON, fontWeight: 400, width: "100%", textAlign: "left",
                    border: "1px solid #d1d5db", background: "#f9fafb",
                  }}
                >
                  Edit {describe(d)}
                </button>
              </li>
            ))}
          </ul>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={onCreateAnother}
            style={{ ...BUTTON, border: "1px solid #d1d5db", background: "white", fontWeight: 400 }}
          >
            Create another draft
          </button>
          {one && (
            <button
              type="button"
              onClick={() => onEdit(drafts[0])}
              style={{ ...BUTTON, border: "none", background: "#111827", color: "white" }}
            >
              Edit existing draft
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
